import React, { useState, useEffect, useRef } from 'react';
import type { City, POI } from '../types';
import { CITIES } from '../constants';

interface SearchControlProps {
  pois: POI[];
  onSelect: (item: City | POI) => void;
  onSearch: (query: string) => void;
}

type Suggestion = {
  id: string;
  name: string;
  subtitle?: string;
  item: City | POI;
};

const SearchIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
);


export const SearchControl: React.FC<SearchControlProps> = ({ pois, onSelect, onSearch }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (value.length > 1) {
      const lowerCaseValue = value.toLowerCase();

      const citySuggestions: Suggestion[] = CITIES.filter(city =>
        city.name.toLowerCase().startsWith(lowerCaseValue)
      ).map(c => ({ id: `city-${c.name}`, name: c.name, subtitle: "Citta'", item: c }));

      const poiSuggestions: Suggestion[] = pois.filter(poi =>
        poi.name.toLowerCase().includes(lowerCaseValue)
      ).map(p => ({
        id: `poi-${p.id}`,
        name: p.name,
        subtitle: p.city || p.region || p.address,
        item: p,
      }));

      const combinedSuggestions = [...citySuggestions, ...poiSuggestions].slice(0, 7);

      setSuggestions(combinedSuggestions);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    setQuery(suggestion.name);
    onSelect(suggestion.item);
    setShowSuggestions(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query);
      setShowSuggestions(false);
    }
  };

  return (
    <form className="relative" ref={searchContainerRef} onSubmit={handleSubmit}>
      <div className="bg-white bg-opacity-80 backdrop-blur-sm p-2 rounded-lg shadow-lg border border-gray-200 flex items-center">
        <div className="pointer-events-none pl-1">
          <SearchIcon />
        </div>
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => query.length > 0 && suggestions.length > 0 && setShowSuggestions(true)}
          placeholder="Cerca citta' o locale..."
          className="w-full bg-transparent text-gray-800 pl-2 focus:outline-none"
          aria-label="Cerca una citta' o un locale"
          autoComplete="off"
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden max-h-80 overflow-y-auto">
          {suggestions.map(suggestion => (
            <li
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-4 py-3 cursor-pointer hover:bg-gray-100 text-gray-800 border-b border-gray-100 last:border-b-0"
            >
              <div className="font-semibold text-sm">{suggestion.name}</div>
              {suggestion.subtitle && <div className="text-xs text-gray-500 mt-0.5">{suggestion.subtitle}</div>}
            </li>
          ))}
        </ul>
      )}
    </form>
  );
};
